<template>
  <div class="tag-instructeur">
    <div v-if="currentInstructeurs.length !== 0" class="InstructionPage-instructeur">
      <div v-for="(instructeur, i) in currentInstructeurs" :key="instructeur.id">
        <b>{{ i + 1 }} - </b>{{ getInstructeurNameLabel(instructeur) }}

        <b-button
            v-if="canRemoveInstructeur(instructeur)"
            icon-left="times"
            :title="$t('instructionPage.remove-instructeur')"
            size="is-small"
            class="remove-button-inscription"
            @click="removeInstructeur(instructeur)"
        />
      </div>
    </div>

    <div v-if="currentInstructeurs.length < 2">
      <b-dropdown
          v-if="canDisplayAddInstructeurButton()"
          aria-role="list"
          @change="selectInstructeur"
      >
        <template #trigger>
          <b-button
              icon-right="plus"
              size="is-small"
              type="is-primary"
              :label="$t('instructionPage.assigner')"
          />
        </template>

        <div v-for="instructeur in instructeurs" :key="instructeur.id">
          <b-dropdown-item aria-role="listitem" :value="instructeur">
            {{ getInstructeurNameLabel(instructeur) }}
          </b-dropdown-item>
        </div>
      </b-dropdown>
    </div>
  </div>
</template>

<script lang="ts">
import {PROFIL_UTILISATEUR} from "@/model/bean/EnumHelper"
import {ProjetInscriptionDTO, UtilisateurDTO} from "@/model/bean/GeneratedDTOs"
import {GraeService} from "@/services/rest/grae/GraeService"
import {UsersService} from "@/services/rest/users/UsersService"
import {defineComponent} from "vue"
import {BButton, BDropdown, BDropdownItem} from "buefy"

export default defineComponent({
  name: "InstructeurView",
  components: {
    BDropdownItem,
    BDropdown,
    BButton,
  },
  props: {
    projet: {
      type: Object,
      required: true,
    },
    loggedUser: {
      type: null as UtilisateurDTO | null,
      required: true,
    },
    typeDossier: {
      type: String,
      required: true,
    },
  },

  emits: ["add-instructeur", "remove-instructeur"],
  data() {
    return {
      graeService: GraeService.INSTANCE,
      usersService: UsersService.INSTANCE,
      internalProjet: new ProjetInscriptionDTO(),
      allInstructeurs: [] as UtilisateurDTO[],
      currentInstructeurs: [] as UtilisateurDTO[],
    }
  },
  computed: {
    loggedUserInstructedGrae(): string[] {
      const instructedGraes = this.loggedUser?.instructedGraes || []
      const userGraeId = this.loggedUser?.grae?.id
      const allGraes = userGraeId ? [...instructedGraes, userGraeId] : instructedGraes
      return [...new Set(allGraes)]
    },
    instructeurs(): UtilisateurDTO[] {
      // Filtre les instructeurs du back-end pour exclure ceux déjà assignés
      const currentInstructeurIds = new Set(this.currentInstructeurs.map((instructeur) => instructeur.id))
      const availableInstructeurs = this.allInstructeurs.filter(
          (instructeur) => !currentInstructeurIds.has(instructeur.id)
      )

      const filteredInstructeurs: UtilisateurDTO[] = []
      // Sépare l'utilisateur connecté des autres pour le mettre en première place
      const loggedUserInstructor = availableInstructeurs.find(
          (instructeur) => instructeur.id === this.loggedUser?.id
      )
      if (loggedUserInstructor) {
        // Place l'utilisateur connecté en première place
        filteredInstructeurs.push(loggedUserInstructor)
      }

      // Ajoute les autres instructeurs disponibles
      const otherInstructeurs = availableInstructeurs.filter(
          (instructeur) => instructeur.id !== this.loggedUser?.id
      )
      filteredInstructeurs.push(...otherInstructeurs)

      return filteredInstructeurs
    }
  },
  created(): void {
    this.init()
  },
  methods: {
    async init(): Promise<void> {
      this.internalProjet = this.projet as any
      const currentInstructeur = this.internalProjet.evaluationFinale.instructeur
      if (
          this.internalProjet.autresInstructeursInscription &&
          this.internalProjet.autresInstructeursInscription.length !== 0
      ) {
        this.currentInstructeurs = [...this.internalProjet.autresInstructeursInscription]
      }
      if (currentInstructeur) {
        this.currentInstructeurs.push(currentInstructeur)
      }
      this.currentInstructeurs = this.currentInstructeurs.reverse()

      const graeId = this.internalProjet.grae.id
      if (this.canDisplayAddInstructeurButton()) {
        this.allInstructeurs = await this.graeService.getInstructeurs(graeId)
      }
    },

    canRemoveInstructeur(instructeur: UtilisateurDTO): boolean {
      const isHimself = instructeur.id === this.loggedUser.id

      switch (this.loggedUser.profil) {
        case PROFIL_UTILISATEUR.SUPER_ADMINISTRATEUR:
        case PROFIL_UTILISATEUR.ACCOMPAGNATEUR_NATIONAL:
          // SuperAdmin et Accompagnateur national peuvent supprimer tout le monde
          return true

        case PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL:
          // Accompagnateur regional ne peut supprimer les utilisateurs que si c'est le même grae que lui (#18564)
          const isInstructedGrae = this.loggedUserInstructedGrae.includes(this.projet.grae.id)
          return isHimself || isInstructedGrae

        case PROFIL_UTILISATEUR.ACCOMPAGNATEUR_REGIONAL:
          // Accompagnateur regional ne peut que se supprimer lui-même
          return isHimself

        default:
          return false
      }
    },

    getInstructeurNameLabel(instructeur: UtilisateurDTO): string {
      if (!instructeur || !instructeur.id) {
        return ""
      }
      return this.loggedUser && instructeur.id === this.loggedUser.id
          ? this.$t("instructionPage.me").toString()
          : `${instructeur.nom} ${instructeur.prenom}`
    },

    canDisplayAddInstructeurButton(): boolean {
      if (!this.internalProjet) {
        return false
      }
      return (
          this.internalProjet.statut &&
          (this.internalProjet.statut == "EN_ATTENTE" ||
              this.internalProjet.statut == "INSTRUCTION_EN_COURS" ||
              this.internalProjet.statut == "INSTRUCTION_TERMINEE" ||
              this.internalProjet.statut == "ACCEPTE_SOUS_RESERVE_REFERENT") &&
          !this.usersService.isObservateurNational(this.loggedUser as any)
      )
    },

    selectInstructeur(instructeur: UtilisateurDTO): void {
      this.$emit("add-instructeur", instructeur, this.projet.id, this.typeDossier)
    },

    removeInstructeur(instructeur: UtilisateurDTO): void {
      this.$emit("remove-instructeur", instructeur, this.projet.id, this.typeDossier)
    },
  },
})
</script>

<style lang="scss" scoped>
.remove-button-inscription {
  visibility: hidden;
}

.InstructionPage-instructeur:hover .remove-button-inscription {
  visibility: visible;
}
</style>
